/**
 * Finance → Rider earnings: per day and rider, what the rides earned.
 *
 * Each row is one rider's day: completed rides, the delivery fees they
 * brought in, the rider's share and the company's commission — every figure
 * read from the deliveries' OWN stored fee and payout, so changing a rate
 * today never restates last month. Earned is shown beside what has actually
 * moved: the fees the rider received and holds for the company (by the custody
 * rule every Finance screen reads), what that leaves owed for the rides, and
 * what customers have paid. A row opens onto the deliveries behind it.

 *
 * Currencies are never added together: a business with an Accra location
 * gets a cedi total beside the naira one.
 */

import { useState, type ReactNode } from 'react';
import {
  DELIVERY_ORIGINS,
  DELIVERY_ORIGIN_LABELS,
  type DeliveryOrigin,
  type RiderEarningsRow,
} from '@cenox/shared';
import { Card, EmptyState, ErrorState, Input, Modal, Select, TableSkeleton } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { useAreaFilter, useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { GeoScopeFields } from '../../components/GeoScope';
import { useRiderDirectory } from '../riderFinance/riderData';
import { useRiderEarnings, useRiderEarningsDeliveries, type EarningsFilters } from './data';


type Preset = 'today' | 'last7' | 'month' | 'custom';

function shift(iso: string, days: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

export function rangeFor(preset: Preset, today: string): { from: string; to: string } {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'last7':
      return { from: shift(today, -6), to: today };
    case 'month':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case 'custom':
      return { from: today, to: today };
  }
}

export function RiderEarningsPage(): ReactNode {
  const { business } = useAuth();
  const timezone = business?.timezone ?? 'Africa/Lagos';
  const today = todayIn(timezone);
  // Country, state and location are the sidebar's, shared by every screen.
  const area = useAreaFilter();
  const scopeParams = useLocationFilter();
  const riders = useRiderDirectory({ status: 'all' });

  const [preset, setPreset] = useState<Preset>('last7');
  const [custom, setCustom] = useState(rangeFor('last7', today));
  const [riderId, setRiderId] = useState('');
  const [source, setSource] = useState<'' | DeliveryOrigin>('');
  const [open, setOpen] = useState<RiderEarningsRow | null>(null);

  const range = preset === 'custom' ? custom : rangeFor(preset, today);

  const scope = {
    // The endpoint's own vocabulary and the request-wide scope, always agreeing.
    ...area,
    ...scopeParams,
    ...(source ? { source } : {}),
  };
  const filters: EarningsFilters = { ...range, ...(riderId ? { riderId } : {}), ...scope };
  const report = useRiderEarnings(filters, useLocationReady());

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Rider earnings</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          What each rider&apos;s completed rides earned, day by day — from each delivery&apos;s own
          recorded fee and payout. Earned is not the same as paid: cash the rider collected is held for
          the company and comes off what the rides leave owed.
        </p>
      </header>

      <Card>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
          <Select
            label="Dates"
            value={preset}
            onChange={(e) => setPreset(e.target.value as Preset)}
            options={[
              { value: 'today', label: 'Today' },
              { value: 'last7', label: 'Last 7 days' },
              { value: 'month', label: 'This month' },
              { value: 'custom', label: 'Custom range' },
            ]}
          />
          {preset === 'custom' ? (
            <>
              <Input
                label="From"
                type="date"
                value={custom.from}
                max={custom.to}
                onChange={(e) => setCustom({ ...custom, from: e.target.value })}
              />
              <Input
                label="To"
                type="date"
                value={custom.to}
                min={custom.from}
                onChange={(e) => setCustom({ ...custom, to: e.target.value })}
              />
            </>
          ) : null}
          <Select
            label="Rider"
            value={riderId}
            onChange={(e) => setRiderId(e.target.value)}
            options={[
              { value: '', label: 'All riders' },
              ...(riders.data?.data ?? [])
                .map((r) => ({ value: r.id, label: r.canonicalName }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            ]}
          />
          <GeoScopeFields bare idPrefix="earnings-scope" />
          <Select
            label="Source"
            value={source}
            onChange={(e) => setSource(e.target.value as '' | DeliveryOrigin)}
            options={[
              { value: '', label: 'All sources' },
              ...DELIVERY_ORIGINS.map((o) => ({ value: o, label: DELIVERY_ORIGIN_LABELS[o] })),
            ]}
          />
        </div>
      </Card>

      {report.isError ? (
        <ErrorState message="Could not load rider earnings." onRetry={() => void report.refetch()} />
      ) : report.isPending ? (
        <TableSkeleton rows={6} columns={8} />
      ) : report.data.rows.length === 0 ? (
        <EmptyState
          title="No completed rides in these dates"
          description="Cancelled, duplicate and deleted deliveries are never counted."
        />
      ) : (
        <Card>
          <div className="-mx-3 overflow-x-auto px-3">
            <table className="w-full min-w-[1080px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Rider</th>
                  <th className="py-2 pr-3 text-right font-medium">Completed rides</th>
                  <th className="py-2 pr-3 text-right font-medium">Gross delivery fees</th>
                  <th className="py-2 pr-3 text-right font-medium">Rider earnings</th>
                  <th className="py-2 pr-3 text-right font-medium">Company commission</th>
                  <th className="py-2 pr-3 text-right font-medium" title="Delivery fees the rider received and holds for the company">Cash with rider</th>
                  <th className="py-2 pr-3 text-right font-medium" title="Rider earnings less cash with rider: positive, the company owes the rider for these rides; negative, the rider owes the company">Net for these rides</th>
                  <th className="py-2 pr-3 text-right font-medium">Paid to rider</th>
                  <th className="py-2 text-right font-medium">Collected from customers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {report.data.rows.map((row) => (
                  <tr
                    key={`${row.date}-${row.riderId ?? row.riderName}-${row.currencyCode}`}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setOpen(row)}
                  >
                    <td className="whitespace-nowrap py-2 pr-3 text-slate-700">{row.date}</td>
                    <td className="py-2 pr-3 font-medium text-slate-900">
                      <button type="button" className="text-left hover:underline" onClick={() => setOpen(row)}>
                        {row.riderName}
                      </button>
                      {row.riderId === null ? (
                        <span className="block text-xs font-normal text-amber-700">Not matched to a rider</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{row.completedRides}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(row.grossDeliveryFees, row.currencyCode)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(row.riderEarnings, row.currencyCode)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-emerald-700">
                      {money(row.companyCommission, row.currencyCode)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {money(row.cashToRider, row.currencyCode)}
                      {row.uncertainCashRides > 0 ? (
                        <span className="block text-xs font-normal text-amber-700" title="Marked paid in cash; nobody recorded who received it. Not deducted.">
                          + {row.uncertainCashRides} cash {row.uncertainCashRides === 1 ? 'ride' : 'rides'}, receiver not recorded
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums"><Net earned={row.riderEarnings} cash={row.cashToRider} currency={row.currencyCode} /></td>
                    <td className="py-2 pr-3 text-right tabular-nums text-slate-500">{money(row.riderPaid, row.currencyCode)}</td>
                    <td className="py-2 text-right tabular-nums text-slate-500">
                      {money(row.customerCollected, row.currencyCode)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {report.data.totals.map((t) => (
                  <tr key={t.currencyCode} className="border-t-2 border-slate-200 font-semibold text-slate-900">
                    <td className="py-2 pr-3" colSpan={2}>
                      Total{report.data.totals.length > 1 ? ` (${t.currencyCode})` : ''} · {t.riders}{' '}
                      {t.riders === 1 ? 'rider' : 'riders'}, {t.days} {t.days === 1 ? 'day' : 'days'}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{t.completedRides}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(t.grossDeliveryFees, t.currencyCode)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(t.riderEarnings, t.currencyCode)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(t.companyCommission, t.currencyCode)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(t.cashToRider, t.currencyCode)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums"><Net earned={t.riderEarnings} cash={t.cashToRider} currency={t.currencyCode} /></td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(t.riderPaid, t.currencyCode)}</td>
                    <td className="py-2 text-right tabular-nums">{money(t.customerCollected, t.currencyCode)}</td>
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>
          {report.data.totals.length > 1 ? (
            <p className="mt-2 text-xs text-slate-500">
              Locations in different currencies are totalled separately. No exchange rate has been set,
              so they are never added together.
            </p>
          ) : null}
        </Card>
      )}

      <DeliveriesModal row={open} scope={scope} onClose={() => setOpen(null)} />
    </div>
  );
}

/** Earnings less cash with the rider, said as who owes whom. Never offsets one rider against another (it is per row). */
function Net({ earned, cash, currency }: { earned: string; cash: string; currency: string }): ReactNode {
  const net = Math.round(Number(earned) * 100) - Math.round(Number(cash) * 100);
  if (net === 0) return <span className="text-slate-500">{money('0', currency)}</span>;
  return net > 0 ? (
    <span className="text-emerald-700" title="The company owes the rider for these rides">{money((net / 100).toFixed(2), currency)}</span>
  ) : (
    <span className="text-red-700" title="The rider owes the company for these rides">−{money((-net / 100).toFixed(2), currency)}</span>
  );
}

function DeliveriesModal({
  row,
  scope,
  onClose,
}: {
  row: RiderEarningsRow | null;
  scope: Omit<EarningsFilters, 'from' | 'to' | 'riderId'>;
  onClose: () => void;
}): ReactNode {
  const deliveries = useRiderEarningsDeliveries(
    row ? { date: row.date, riderId: row.riderId, riderName: row.riderName } : null,
    scope,
  );
  return (
    <Modal open={row !== null} onClose={onClose} title={row ? `${row.riderName} — ${row.date}` : ''} size="lg">
      {deliveries.isError ? (
        <ErrorState message="Could not load the deliveries." onRetry={() => void deliveries.refetch()} />
      ) : deliveries.isPending ? (
        <TableSkeleton rows={4} columns={6} />
      ) : (
        <div className="-mx-3 overflow-x-auto px-3">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-3 font-medium">Reference</th>
                <th className="py-2 pr-3 font-medium">Source</th>
                <th className="py-2 pr-3 text-right font-medium">Fee</th>
                <th className="py-2 pr-3 text-right font-medium">Rider payout</th>
                <th className="py-2 pr-3 text-right font-medium">Commission</th>
                <th className="py-2 font-medium">Customer payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {deliveries.data.data.map((d) => (
                <tr key={d.id}>
                  <td className="py-2 pr-3 text-slate-700">{d.reference ?? '—'}</td>
                  <td className="py-2 pr-3 text-slate-500">
                    {DELIVERY_ORIGIN_LABELS[d.source as DeliveryOrigin] ?? d.source}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{money(d.fee, d.currencyCode)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{money(d.riderPayout, d.currencyCode)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{money(d.commission, d.currencyCode)}</td>
                  <td className="py-2 text-slate-500">{d.paymentStatus || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
